import React, { useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import Markdown from 'react-native-markdown-display';

const HelpScreen = () => {
  const [isTechnicalExpanded, setIsTechnicalExpanded] = useState(false);

  const toggleTechnicalSection = () => {
    setIsTechnicalExpanded(!isTechnicalExpanded);
  };

  const basicUserGuide = `
# Trials Live Scoring - Quick Start Guide

## 🇬🇧 Basic User Guide

### Getting Started

**Trials Live Scoring** helps you score UCI cycling Trial races in real-time on your phone, tablet, or computer.

#### 1. Login
- **Enter your email** and **password** (provided by race organizers)
- **Scan QR Code**: If available, scan for instant login
- Sessions last 24 hours with automatic warnings

#### 2. Choose Your Race
- **Select a competition** from the list
- **Pull down to refresh** if you don't see new races
- **Search** by typing the race name

#### 3. Assign Your Section
- **First time**: Choose your assigned section (S1, S2, S3, etc.)
- **Red dot indicator** marks your section
- **Remembered**: Your section is saved for future use

#### 4. Select a Rider
- **Choose a pilot** from the list
- **Search** by name or number
- **Your section** is shown at the top

### Scoring Process

#### Section & Lap Selection
- **Your Section**: Displayed with red dot indicator
- **Change Section**: Tap the section button to modify assignment
- **Select Lap**: Choose L1, L2, L3, etc.
- **Visual Indicators**:
  - 🟢 **Green dot**: Lap complete (all sections scored)
  - 🟠 **Orange dot**: Lap in progress (partially scored)
  - ⚪ **No dot**: Lap not started
  - 🔵 **Blue outline**: Currently selected

#### Assigning Points
- **Select section and lap first** (marked with *)
- **Choose score**: 0, 10, 20, 30, 40, 50, 60, or - (not scored)
- **Current score** highlighted in blue
- **Automatic saving** to Google Sheets
- **Total score** calculated automatically

#### Correcting Mistakes
- **Existing scores**: Confirmation popup before changing
- **Live scoring**: No interruption during real-time judging
- **Wrong section**: App prevents scoring in other sections

#### Score Consultation Mode
- **Tap Total Score bar** to view all sections and laps
- **Eye icon** shows consultation is active
- **Read-only**: Cannot modify scores while consulting
- **Your section** highlighted with red border
- **Exit**: Tap Total Score bar again

#### Auto-Lap Features
- **Automatic progression** to next lap when current is complete
- **Smart start**: Begins with first incomplete lap
- **Notifications**: Optional popups for completed laps
- **Haptic feedback**: Vibration confirmation

### Navigation Tips
- **Pull down** to refresh data
- **Search automatically clears** when leaving screens
- **Horizontal swipes** for smooth navigation
- **Install as app** for better performance

---

## 🇫🇷 Guide Utilisateur de Base

### Démarrage

**Trials Live Scoring** vous aide à noter les courses de Trial UCI en temps réel sur votre téléphone, tablette ou ordinateur.

#### 1. Connexion
- **Saisissez votre email** et **mot de passe** (fournis par les organisateurs)
- **Scanner le QR Code** : Si disponible, scanner pour une connexion instantanée
- Les sessions durent 24 heures avec avertissements automatiques

#### 2. Choisir Votre Course
- **Sélectionnez une compétition** dans la liste
- **Tirez vers le bas pour actualiser** si vous ne voyez pas les nouvelles courses
- **Recherchez** en tapant le nom de la course

#### 3. Assigner Votre Zone
- **Première fois** : Choisissez votre zone assignée (S1, S2, S3, etc.)
- **Indicateur point rouge** marque votre zone
- **Mémorisé** : Votre zone est sauvegardée pour un usage futur

#### 4. Sélectionner un Pilote
- **Choisissez un pilote** dans la liste
- **Recherchez** par nom ou numéro
- **Votre zone** est affichée en haut

### Processus de Notation

#### Sélection Zone & Tour
- **Votre Zone** : Affichée avec indicateur point rouge
- **Changer de Zone** : Appuyez sur le bouton zone pour modifier l'assignation
- **Sélectionner Tour** : Choisissez L1, L2, L3, etc.
- **Indicateurs Visuels** :
  - 🟢 **Point vert** : Tour complet (toutes zones notées)
  - 🟠 **Point orange** : Tour en cours (partiellement noté)
  - ⚪ **Pas de point** : Tour non commencé
  - 🔵 **Contour bleu** : Actuellement sélectionné

#### Attribution des Points
- **Sélectionnez zone et tour d'abord** (marqués avec *)
- **Choisissez le score** : 0, 10, 20, 30, 40, 50, 60, ou - (non noté)
- **Score actuel** surligné en bleu
- **Sauvegarde automatique** vers Google Sheets
- **Score total** calculé automatiquement

#### Corriger les Erreurs
- **Scores existants** : Popup de confirmation avant modification
- **Notation en direct** : Aucune interruption pendant le jugement en temps réel
- **Mauvaise zone** : L'app empêche la notation dans d'autres zones

#### Mode Consultation des Scores
- **Appuyez sur la barre Score Total** pour voir toutes les zones et tours
- **Icône œil** montre que la consultation est active
- **Lecture seule** : Impossible de modifier les scores en consultation
- **Votre zone** surlignée avec bordure rouge
- **Sortir** : Appuyez à nouveau sur la barre Score Total

#### Fonctionnalités Auto-Tour
- **Progression automatique** vers le tour suivant quand le tour actuel est terminé
- **Démarrage intelligent** : Commence avec le premier tour incomplet
- **Notifications** : Popups optionnels pour les tours terminés
- **Retour haptique** : Confirmation par vibration

### Conseils de Navigation
- **Tirez vers le bas** pour actualiser les données
- **La recherche se vide automatiquement** en quittant les écrans
- **Glissements horizontaux** pour une navigation fluide
- **Installer comme app** pour de meilleures performances

---

## Advanced Features & Setup

### About the Application

Trials Live Scoring is a progressive web application for scoring UCI cycling Trial races in real-time. It connects to Google Sheets to store and retrieve course data, allowing multiple judges and scorekeepers to collaborate simultaneously across different devices.

### Authentication System

The app uses **JWT-based authentication** with enhanced security:

1. **Login Methods:**
   - Standard email/password from Authorizations sheet
   - QR Code scanning for instant authentication
   - 24-hour JWT sessions with device fingerprinting

2. **User Roles:**
   - **Standard Users:** Access to scoring and basic app features
   - **Administrators:** Additional privileges including server cache management
   - Role display with admin badge in Settings

3. **Security Features:**
   - Session management with 30-minute expiration warnings
   - Device binding for enhanced security
   - Automatic session cleanup

### Google Sheets Setup

1. **Sheet Structure:**
   - Each course has its own sheet (tab)
   - "Authorizations" sheet contains user access data
   - Required columns: POS, RiderNumber, RiderName, Country, LAP1_S1, LAP1_S2, etc.

2. **Authorizations Format:**
   - Columns: \`email\`, \`password\`, \`name\`, \`username\`, \`admin\`
   - Admin column: "true", "1", or "yes" for administrator privileges

### Enhanced Interface Features

#### Progressive Web App Installation
- **Mobile:** "Add to Home Screen" feature
- **Desktop:** Install button in browser address bar
- **Benefits:** Offline viewing, faster loading, app-like experience
- **Hash Routing:** Improved compatibility across hosting platforms

#### Modern Navigation
- **Sticky Headers:** Navigation bars remain visible while scrolling (web)
- **Horizontal Transitions:** Smooth left-to-right screen changes
- **Auto-clearing Search:** Search fields reset automatically
- **Smart Caching:** 5-minute cache for improved performance

#### Multi-User Collaboration
- **Section Assignment System:** Each user assigned to specific sections
- **Real-time Synchronization:** Changes visible across all devices instantly
- **Conflict Prevention:** Visual warnings for cross-section scoring
- **Session Monitoring:** Track multiple user sessions simultaneously

### Data Management

#### Smart Caching System
- **Client Cache:** 5-minute browser/device cache for faster loading
- **Server Cache:** Reduced Google Sheets API calls
- **Manual Cache Control:** Clear options in Settings
- **Real-time Scoring:** Live updates bypass cache for accuracy

#### Score Protection
- **Existing Score Warnings:** Confirmation for modifying established scores
- **Live Scoring Freedom:** Uninterrupted scoring during active judging
- **Consultation Mode:** Safe viewing without modification risk
- **Auto-Lap Progression:** Intelligent advancement through incomplete laps

### Settings & Configuration

#### Account Management
- **Session Information:** JWT token details, expiration times
- **Cache Management:** Client and server cache clearing options
- **Section Memory:** Clear and reassign section preferences
- **Debug Tools:** Advanced diagnostic information

#### Haptic Feedback
- **Customizable:** Light, Medium, Strong, or Sound (iOS)
- **Action-Specific:** Different patterns for different interactions
- **Auto-Lap Feedback:** Special vibration for automatic progression
- **Platform Support:** Optimized for mobile and desktop

### Troubleshooting

#### Common Issues
- **Connection Problems:** Use "Clear Cache" in Settings for fresh data
- **Authentication:** Verify credentials in Authorizations sheet
- **Section Conflicts:** Use consultation mode to safely view other sections
- **Performance:** Install as PWA, use stable Wi-Fi connection

#### Error Recovery
- **Retry Mechanisms:** Countdown timers prevent spam clicking
- **Session Management:** Automatic reconnection for expired sessions
- **Cache Recovery:** Multiple cache clearing options available
- **Admin Support:** Enhanced diagnostic tools for administrators

### Best Practices

#### For Accurate Scoring
- Stick to your assigned section to prevent conflicts
- Use consultation mode to safely view other sections
- Verify section and lap selection before scoring
- Pay attention to haptic feedback and visual indicators

#### For Optimal Performance
- Install as PWA on mobile devices
- Use stable Wi-Fi connection
- Clear cache when data appears outdated
- Enable haptic feedback for better interaction

#### For Team Coordination
- Coordinate section assignments before events
- Use QR codes for quick team authentication
- Monitor session status to prevent expiration
- Communicate when clearing server cache (admin)

### Technical Requirements

- **Mobile:** iOS 13+ (Safari), Android 7+ (Chrome)
- **Desktop:** Chrome 90+, Firefox 88+, Safari 14+, Edge 90+
- **Network:** Stable internet for authentication and score sync
- **Storage:** 50MB for app cache and data

### Contact & Support

- Contact race administrators for access and QR codes
- Report issues to development team
- Check for regular app updates

### Version History

- **v1.5.5:** Complete help documentation update, enhanced user guide with bilingual support
- **v1.5.4:** JWT authentication, server cache management, admin privileges, QR code system
- **v1.5.3:** Section assignment system, score consultation mode, enhanced UI protection
- **v1.5.2:** Auto-lap progression, section memory, visual status indicators
- **v1.5.1:** Performance optimizations, enhanced error handling
- **v1.5.0:** Auto-lap features, score protection, API caching, enhanced haptics

*This application is designed for UCI Trials races following official scoring guidelines.*
`;

  const technicalHelpContent = `
## Technical Information & Developer Guide

### Architecture & Authentication

#### JWT Authentication System
- **Security:** Industry-standard JSON Web Tokens with HS256 algorithm
- **Session Management:** 24-hour token expiration with automatic renewal warnings
- **Device Fingerprinting:** Unique device identification for security tracking
- **Multi-Platform:** Consistent authentication across web, mobile, and desktop

#### Token Structure
- **Payload:** Email, Sheet ID, admin status, device ID, session ID
- **Expiration:** Automatic cleanup with grace period warnings
- **Validation:** Real-time token verification with fallback mechanisms
- **Storage:** Secure local storage with automatic cleanup on logout

### API & Backend Integration

#### Google Sheets Integration
- **Service Account Authentication:** Server-side Google API integration
- **Real-Time Sync:** Immediate score updates across all connected devices
- **Rate Limiting:** Built-in request throttling to prevent API quota issues
- **Error Handling:** Comprehensive retry mechanisms with exponential backoff

#### Caching Architecture
- **Client Cache:** 5-minute browser/device cache for lists and pilot data
- **Server Cache:** Server-side caching for reduced Google Sheets API calls
- **Cache Invalidation:** Manual and automatic cache clearing mechanisms
- **Smart Refresh:** Selective cache updates based on data type and age

#### API Endpoints
\`\`\`
POST /auth/login          # JWT authentication
GET  /session/info        # Session status and details
POST /cache/clear         # Server cache management (admin)
GET  /sheets/{id}/list    # Competition listing
GET  /sheets/{id}/riders  # Pilot data retrieval
GET  /sheets/{id}/structure # Course structure (laps/sections)
GET  /sheets/{id}/scores  # Individual score fetching
PUT  /sheets/{id}/score   # Score update submission
\`\`\`

### Security & Access Control

#### User Role Management
- **Admin Users:** Full system access including server cache management
- **Standard Users:** Scoring access with section assignment restrictions
- **Role Definition:** Configured in Google Sheets Authorizations tab
- **Permission Validation:** Server-side role verification for all admin operations

#### Security Features
- **Session Tokens:** Cryptographically signed JWT tokens
- **Device Binding:** Session tied to specific device fingerprints
- **Automatic Expiration:** 24-hour maximum session duration
- **Secure Headers:** CORS, XSS, and content-type protections

### Advanced Configuration

#### Feature Flags
\`\`\`javascript
export const config = {
  // Auto-lap features
  ENABLE_AUTO_LAP_SELECTION: true,
  ENABLE_AUTO_LAP_NOTIFICATIONS: true,
  AUTO_LAP_FEEDBACK: true,
  
  // Section management
  ENABLE_SECTION_MEMORY: true,
  
  // UI features
  ENABLE_STICKY_HEADER: true,
  ENABLE_STICKY_SCORING_HEADER: true,
  
  // Performance settings
  RETRY_COUNTDOWN_SECONDS: 20,
  CLIENT_CACHE_TIMEOUT: 300000, // 5 minutes
};
\`\`\`

### Development & Debugging

#### Debug Tools
- **JWT Token Inspector:** Decode and validate authentication tokens
- **Session Diagnostics:** Real-time session status monitoring
- **Cache Inspector:** View and manipulate cache contents
- **Network Monitor:** API request/response logging

#### Admin Debug Functions
\`\`\`javascript
// Available in browser console for admin users
window.debugTrials = {
  diagnose: () => diagnostics.runFullSystemCheck(),
  cleanCache: () => cache.clearAllCaches(),
  validateToken: () => auth.validateCurrentToken(),
  sessionInfo: () => auth.getDetailedSessionInfo(),
  clearServerCache: () => admin.clearGlobalCache(),
};
\`\`\`

### Deployment & Hosting

#### Progressive Web App Features
- **Service Worker:** Offline capability and cache management
- **Manifest Configuration:** Install prompts and home screen integration
- **Hash Routing:** SPA compatibility with static hosting platforms
- **Universal 404 Handling:** Automatic redirection for direct route access

#### Build & Export Process
\`\`\`bash
# Development
npm start

# Production build
npm run export:web

# Local testing
npm run serve
\`\`\`

### Performance Optimization

#### Client-Side Optimization
- **Bundle Size:** JavaScript payload minimization
- **Render Performance:** React component optimization
- **Memory Management:** Efficient state management
- **Network Optimization:** Request batching and caching

#### Server-Side Optimization
- **API Caching:** Reduced Google Sheets API calls
- **Rate Limiting:** Quota management and throttling
- **Error Handling:** Comprehensive retry mechanisms
- **Session Management:** Efficient JWT token validation

### API Integration Examples

#### Authentication Flow
\`\`\`javascript
const response = await fetch('/api/auth/login', {
  method: 'POST',
  headers: { 'Content-Type': 'application/json' },
  body: JSON.stringify({ email, password, sheetId })
});

const { token, email, isAdmin } = await response.json();
\`\`\`

#### Score Update Process
\`\`\`javascript
const response = await fetch('/api/sheets/{id}/score', {
  method: 'PUT',
  headers: {
    'Authorization': \`Bearer \${token}\`,
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    sheetName, riderName, lapSection, score
  })
});
\`\`\`

#### Cache Management
\`\`\`javascript
// Client cache clearing
sheetService.clearCache();

// Server cache clearing (admin only)
const response = await fetch('/api/cache/clear', {
  method: 'POST',
  headers: { 'Authorization': \`Bearer \${adminToken}\` }
});
\`\`\`
`;

  return (
    <SafeAreaView style={styles.container} edges={['bottom']}>
      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Basic User Guide Section */}
        <Markdown style={markdownStyles}>
          {basicUserGuide}
        </Markdown>

        {/* Technical Section - Collapsible */}
        <View style={styles.technicalSection}>
          <TouchableOpacity
            style={styles.technicalHeader}
            onPress={toggleTechnicalSection}
          >
            <View style={styles.technicalHeaderContent}>
              <Ionicons name="settings-outline" size={24} color="#0066cc" />
              <Text style={styles.technicalHeaderTitle}>
                Technical Information & Developer Guide
              </Text>
              <Ionicons 
                name={isTechnicalExpanded ? "chevron-up" : "chevron-down"} 
                size={24} 
                color="#0066cc" 
              />
            </View>
          </TouchableOpacity>

          {isTechnicalExpanded && (
            <View style={styles.technicalContent}>
              <Markdown style={technicalMarkdownStyles}>
                {technicalHelpContent}
              </Markdown>
            </View>
          )}
        </View>

        <View style={styles.footer}>
          <Text style={styles.copyright}>© Gabriele Fusco / VTM 2025</Text>
          <Text style={styles.version}>App Version 1.5.5 • Help Updated January 2025</Text>
        </View>
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  scrollContent: {
    padding: 16,
    paddingBottom: 40,
  },
  technicalSection: {
    marginTop: 32,
    borderTopWidth: 2,
    borderTopColor: '#e0e0e0',
    paddingTop: 16,
  },
  technicalHeader: {
    backgroundColor: '#f8f9fa',
    borderRadius: 12,
    padding: 16,
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e9ecef',
  },
  technicalHeaderContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  technicalHeaderTitle: {
    fontSize: 18,
    
    color: '#0066cc',
    fontFamily: 'Montserrat_700Bold',
    flex: 1,
    marginLeft: 12,
  },
  technicalContent: {
    backgroundColor: '#f8f9fa',
    borderRadius: 8,
    padding: 16,
    borderLeftWidth: 4,
    borderLeftColor: '#0066cc',
  },
  footer: {
    marginTop: 32,
    alignItems: 'center',
    paddingTop: 16,
    borderTopWidth: 1,
    borderTopColor: '#e0e0e0',
  },
  copyright: {
    fontSize: 12,
    color: '#888',
    fontFamily: 'Montserrat_400Regular',
  },
  version: {
    fontSize: 10,
    color: '#aaa',
    fontFamily: 'Montserrat_400Regular',
    marginTop: 4,
  },
});

const markdownStyles = {
  body: {
    fontSize: 16,
    lineHeight: 24,
    color: '#333',
    fontFamily: 'Montserrat_400Regular',
  },
  heading1: {
    fontSize: 28,
    
    color: '#0066cc',
    marginBottom: 16,
    marginTop: 24,
    fontFamily: 'Montserrat_700Bold',
  },
  heading2: {
    fontSize: 22,
    
    color: '#333',
    marginBottom: 12,
    marginTop: 24,
    fontFamily: 'Montserrat_700Bold',
  },
  heading3: {
    fontSize: 18,
    
    color: '#555',
    marginBottom: 8,
    marginTop: 16,
    fontFamily: 'Montserrat_700Bold',
  },
  heading4: {
    fontSize: 16,
    
    color: '#666',
    marginBottom: 6,
    marginTop: 12,
    fontFamily: 'Montserrat_600SemiBold',
  },
  paragraph: {
    marginBottom: 16,
    fontFamily: 'Montserrat_400Regular',
  },
  list_item: {
    marginBottom: 8,
    fontFamily: 'Montserrat_400Regular',
  },
  bullet_list: {
    marginBottom: 16,
  },
  ordered_list: {
    marginBottom: 16,
  },
  code_inline: {
    backgroundColor: '#f5f5f5',
    paddingHorizontal: 4,
    paddingVertical: 2,
    fontFamily: 'monospace',
    color: '#0066cc',
    fontSize: 14,
  },
  code_block: {
    backgroundColor: '#f5f5f5',
    padding: 12,
    borderRadius: 4,
    fontFamily: 'monospace',
    marginBottom: 16,
    fontSize: 12,
  },
  fence: {
    backgroundColor: '#f5f5f5',
    padding: 12,
    borderRadius: 4,
    fontFamily: 'monospace',
    marginBottom: 16,
    fontSize: 12,
  },
  blockquote: {
    borderLeftWidth: 4,
    borderLeftColor: '#0066cc',
    paddingLeft: 12,
    marginLeft: 0,
    marginRight: 0,
    marginVertical: 16,
    backgroundColor: '#f8f9fa',
    padding: 12,
    borderRadius: 4,
  },
  strong: {
    
    fontFamily: 'Montserrat_700Bold',
  },
  em: {
    fontStyle: 'italic',
  },
  hr: {
    backgroundColor: '#e0e0e0',
    height: 1,
    marginVertical: 16,
  },
};

const technicalMarkdownStyles = {
  ...markdownStyles,
  body: {
    ...markdownStyles.body,
    fontSize: 14,
    lineHeight: 20,
  },
  heading1: {
    ...markdownStyles.heading1,
    fontSize: 24,
    color: '#495057',
  },
  heading2: {
    ...markdownStyles.heading2,
    fontSize: 20,
    color: '#495057',
  },
  heading3: {
    ...markdownStyles.heading3,
    fontSize: 16,
    color: '#6c757d',
  },
  heading4: {
    ...markdownStyles.heading4,
    fontSize: 14,
    color: '#6c757d',
  },
  code_block: {
    ...markdownStyles.code_block,
    backgroundColor: '#e9ecef',
    fontSize: 11,
  },
  fence: {
    ...markdownStyles.fence,
    backgroundColor: '#e9ecef',
    fontSize: 11,
  },
};

export default HelpScreen;